/*
 * Cart drawer "You may also like" row from a cart-anchored Layers block.
 *
 * The block (Cart – Frequently Bought Together, filled from the accessories
 * collection) recommends from every product in the cart. Liquid renders the
 * accessories cards first, so the row works without Layers; once the block
 * answers, its products replace them in the same `.upsell-card` markup that
 * assets/cart-drawer-upsell.js wires to add-to-cart. The cart drawer section
 * re-renders on every cart change, so each render connects a fresh element
 * with the current cart's product IDs. While the drawer is closed the request
 * waits until it opens or the shopper reaches for the cart toggle.
 */

import { escapeHtml, formatMoney, getSDK, mediaUrl, mediaResponseOptions, slimProducts } from './layers.js';

const DEFAULT_LIMIT = 8;
// Each option's data-image swaps the card image on selection, so this request
// keeps every variant's image (cards only need the product's).
const UPSELL_ATTRIBUTES = ['variants[].featured_media'];

function cents(price) {
  const amount = typeof price === 'object' && price !== null ? price.amount : price;
  const value = Number(amount);
  return Number.isFinite(value) ? Math.round(value * 100) : null;
}

function variantId(variant) {
  return variant?.numericId ?? String(variant?.gid || variant?.id || '').split('/').pop();
}

function imageFor(product, variant) {
  const media = variant?.image || product.featuredMedia || product.images?.[0];
  return media?.url || media?.src || '';
}

/* One `.upsell-card`, matching snippets/upsell-block.liquid. */
export function upsellCardMarkup(product) {
  const variants = (product.variants || []).filter((variant) => variant.availableForSale ?? variant.available);
  if (variants.length === 0) return '';

  const title = escapeHtml(product.title);
  const image = imageFor(product, variants[0]);
  const imageMarkup = image ? `<img src="${escapeHtml(mediaUrl(image, 400))}" alt="${title}" loading="lazy">` : '';

  let details;
  if (variants.length > 1) {
    const options = variants
      .map((variant) => {
        const variantImage = imageFor(product, variant);
        return `<option value="${escapeHtml(variantId(variant))}"${variantImage ? ` data-image="${escapeHtml(mediaUrl(variantImage, 400))}"` : ''}>${escapeHtml(variant.title)} — ${escapeHtml(formatMoney(cents(variant.price)))}</option>`;
      })
      .join('');
    details = `<select class="upsell-variant-select" aria-label="Select a variant for ${title}">${options}</select>`;
  } else {
    details = `<div class="upsell-price">${escapeHtml(formatMoney(cents(variants[0].price)))}</div>
      <input type="hidden" class="upsell-variant-id" value="${escapeHtml(variantId(variants[0]))}">`;
  }

  return `<div class="upsell-card" data-handle="${escapeHtml(product.handle)}">
    <div class="upsell-image">${imageMarkup}</div>
    <div class="upsell-info">
      <h5 class="upsell-title-main">${title}</h5>
      ${details}
    </div>
    <button type="button" class="upsell-add-btn" aria-label="Add ${title} to cart">ADD TO CART</button>
  </div>`;
}

export class LayersCartUpsell extends HTMLElement {
  connectedCallback() {
    if (this.loadStarted) return;
    const drawer = this.closest('cart-drawer');
    // Outside a drawer, or re-rendered inside an open one by a cart change.
    if (!drawer || drawer.hasAttribute('open')) {
      this.startLoad();
      return;
    }

    // A closed drawer waits for the shopper: opening it, or reaching for the
    // cart toggle first, which starts the request before the click lands.
    this.intent?.abort();
    this.intent = new AbortController();
    const { signal } = this.intent;
    const start = () => this.startLoad();
    drawer.addEventListener('dialog:before-show', start, { signal });
    const toggle = `[aria-controls="${CSS.escape(drawer.id || 'cart-drawer')}"]`;
    ['pointerover', 'focusin', 'touchstart'].forEach((type) =>
      document.addEventListener(
        type,
        (event) => {
          if (event.target instanceof Element && event.target.closest(toggle)) start();
        },
        { signal, passive: true },
      ),
    );
  }

  disconnectedCallback() {
    // Opening moves the drawer to <body>, which reconnects this element; an
    // unstarted wait re-arms then, and a removed element stops listening.
    this.intent?.abort();
  }

  startLoad() {
    if (this.loadStarted) return;
    this.loadStarted = true;
    this.intent?.abort();
    this.load();
  }

  get cartProductIds() {
    try {
      const ids = JSON.parse(this.dataset.cartProductIds || '[]');
      return Array.isArray(ids) ? ids.map(Number).filter(Number.isFinite) : [];
    } catch {
      return [];
    }
  }

  async load() {
    const blockId = this.dataset.blockId;
    const row = this.querySelector('.upsell-row');
    const productIds = this.cartProductIds;
    if (!blockId || !row || productIds.length === 0) return;

    const limit = Math.max(1, Number(this.dataset.limit) || DEFAULT_LIMIT);
    let data, error;
    try {
      const sdk = await getSDK();
      ({ data, error } = await sdk
        .blocks({ blockId, excludeProductIds: productIds.map(String), transformBody: slimProducts(undefined, UPSELL_ATTRIBUTES) })
        .get({
          pagination: { page: 1, limit },
          // The cart in the request (not only the pixel's) keys the SDK cache
          // per cart, so a changed cart never reuses stale picks.
          context: { productsInCart: productIds.map((productId) => ({ productId })) },
          responseOptions: mediaResponseOptions({ enabled: false }),
        }));
    } catch (requestError) {
      error = requestError;
    }

    if (!this.isConnected) return;
    if (error) {
      // Keep the server-rendered accessories.
      console.error('Layers cart recommendations failed:', error);
      return;
    }

    const inCart = new Set(productIds);
    const cards = (data?.products || [])
      .filter((product) => !inCart.has(Number(product.numericId ?? product.id)))
      .slice(0, limit)
      .map(upsellCardMarkup)
      .filter(Boolean);
    if (cards.length === 0) return;

    row.innerHTML = cards.join('');
    this.dataset.source = 'layers';
  }
}

if (typeof window !== 'undefined' && !window.customElements.get('layers-cart-upsell')) {
  window.customElements.define('layers-cart-upsell', LayersCartUpsell);
}
